import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import DateFormat from './DateFormat';
import TimeCalculator from './TimeCalculator';

const FONT_SIZE = 12;
const FONT_SIZE_HEAD_LINE = 14;
const LINE_HIGHT = 1.1;
const OFFSET_X = 25;
const OFFSET_Y = 25;
const FONT = StandardFonts.Helvetica;
const FONT_BOLD = StandardFonts.HelveticaBold;
const COLUMN_WIDTH = 110;

class PDFState {

    constructor(
        pageWidth,
        pageHeight,
        paddingX,
        paddingY,
        lineHight,
        font,
        fontBold,
        fontSize,
        fontSizeHeadLine,
        color,
        columWidth,
    ) {
        this.pageWidth = pageWidth;
        this.pageHeight = pageHeight;
        this.paddingX = paddingX;
        this.paddingY = paddingY;
        this.lineHight = lineHight;
        this.activeFont = font;
        this.font = font;
        this.fontBold = fontBold;
        this.activeFontSize = fontSize;
        this.fontSize = fontSize;
        this.fontSizeHeadLine = fontSizeHeadLine;
        this.color = color;
        this.columnWidth = columWidth;
        this.currentLine = 1;
    };

    newLine() {
        this.currentLine += 1;
    };

    getY() {
        return this.pageHeight - this.paddingY - (this.currentLine * this.lineHight * this.fontSize);
    };

    getX(offset = 0) {
        return this.paddingX + offset;
    };

    isSufficientSpace(entry_count) {
        let space = (2 * entry_count) * this.lineHight * this.activeFontSize;
        let remainingHight = this.getY() - this.paddingY;

        return (remainingHight - space) > 0;
    };

    clear() {
        this.currentLine = 1;
    };

    setFontBold() {
        this.activeFont = this.fontBold;
    };

    setFontNormal() {
        this.activeFont = this.font;
    };

    setFontSizeLarge() {
        this.activeFontSize = this.fontSizeHeadLine;
    };

    setFontSizeNormal() {
        this.activeFontSize = this.fontSize;
    };

    getOptions() {
        return {
            x: this.getX(),
            y: this.getY(),
            font: this.activeFont,
            size: this.activeFontSize,
            color: this.color,
        };
    };

    getOptionsTextCenter(text) {
        let remainingWidth = this.pageWidth - this.font.widthOfTextAtSize(text, this.activeFontSize);
        let x_offset = remainingWidth / 2;

        return {
            x: x_offset,
            y: this.getY(),
            font: this.activeFont,
            size: this.activeFontSize,
            color: this.color,
        };
    };

    getOptionsDateColumn() {
        return {
            x: this.getX(),
            y: this.getY(),
            font: this.activeFont,
            size: this.activeFontSize,
            color: this.color,
        };
    };

    getOptionsTimeColumn() {
        return {
            x: this.getX(this.columnWidth),
            y: this.getY(),
            font: this.activeFont,
            size: this.activeFontSize,
            color: this.color,
        };
    };

    getOptionsHoursColumn() {
        return {
            x: this.getX(2 * this.columnWidth),
            y: this.getY(),
            font: this.activeFont,
            size: this.activeFontSize,
            color: this.color,
        };
    };

    getOptionsBreakColumn() {
        return {
            x: this.getX(3 * this.columnWidth),
            y: this.getY(),
            font: this.activeFont,
            size: this.activeFontSize,
            color: this.color,
        };
    };
};

const generatePDFfromRecords = async (workRecords, withDetails, start, end, generatedBy) => {
    let pdfBlobs = [];

    for (let record of workRecords) {

        let blob = await generatePDFfromRecord(record, withDetails, start, end, generatedBy);
        pdfBlobs.push(blob);

    }

    return pdfBlobs;
};

const generatePDFfromRecord = async (workRecord, withDetails, start, end, generatedBy) => {
    let pdf = await createPDF();
    let pdfState = await createPdfState(pdf);

    writeHeadline({ pdf, pdfState }, workRecord, start, end, generatedBy);

    pdfState.newLine();
    pdfState.newLine();

    writeColumnHeader({ pdf, pdfState });
    workRecord.workDays.sort((a, b) => a.date - b.date)
    for (const workDay of workRecord.workDays) {
        writeDay({ pdf, pdfState }, workDay, withDetails);
    };

    const pdfBytes = await pdf.save();
    const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });

    return pdfBlob;
};

const createPDF = async () => {
    const pdf = await PDFDocument.create();

    return pdf;
};

const createPdfState = async (pdf) => {
    const page = pdf.addPage();
    const { width, height } = page.getSize();
    const font = await pdf.embedFont(FONT);
    const fontBold = await pdf.embedFont(FONT_BOLD);

    let options = new PDFState(
        width,
        height,
        OFFSET_X,
        OFFSET_Y,
        LINE_HIGHT,
        font,
        fontBold,
        FONT_SIZE,
        FONT_SIZE_HEAD_LINE,
        rgb(0, 0, 0),
        COLUMN_WIDTH,
    );

    return options;
};

const writeHeadline = ({ pdf, pdfState }, workRecord, start, end, generatedBy) => {
    let name = workRecord.name;
    const page = pdf.getPage(pdf.getPages().length - 1);
    let today = new Date()

    pdfState.setFontSizeLarge();
    pdfState.setFontBold();
    
    let temporary = '';
    
    if(workRecord.workDays.length > 0 && typeof workRecord.workDays[0].submitted !== 'undefined' && !workRecord.workDays[0].submitted){
        temporary = 'Vorläufiger';
    };

    let lines = [
        `${temporary} Arbeitsnachweis ${name}`,
        `von ${DateFormat.formatDDMMYY(start)}`,
        `bis ${DateFormat.formatDDMMYY(end)}`,
    ];

    let subtitle = `Erzeugt am ${today.toLocaleDateString('de-de', { day: '2-digit', month: '2-digit', year: 'numeric' })} um ${today.toLocaleTimeString('de-de', { hour: '2-digit', minute: '2-digit' })} Uhr von ${generatedBy}`;

    for (let line of lines) {
        let opts = pdfState.getOptionsTextCenter(line);
        write(page, opts, line);
        pdfState.newLine();
    };
    pdfState.newLine();
    pdfState.setFontSizeNormal();
    pdfState.setFontNormal();
    let opts = pdfState.getOptionsTextCenter(subtitle)
    write(page, opts, subtitle)
    pdfState.newLine()
};

const writeDay = ({ pdf, pdfState }, workDay, withDetails) => {
    if (withDetails && !pdfState.isSufficientSpace(workDay.workEntries.length)) {
        pdf.addPage();
        pdfState.clear();
        writeColumnHeader({ pdf, pdfState });
    } else if (!withDetails && !pdfState.isSufficientSpace(1)) {
        pdf.addPage();
        pdfState.clear();
        writeColumnHeader({ pdf, pdfState });
    };

    const {
        day,
        start,
        end,
        sumWorkDurations,
        breakDurations,
        leadingBreakDuration,
        trailingBreakDuration,
        sumBreakDurations,
    } = TimeCalculator.computeTimesFromWorkDay(workDay);

    const date = `${DateFormat.formatWeekDay(day)} ${DateFormat.formatDDMM(day)}`;
    const time = `${DateFormat.formatHHMM(start)} - ${DateFormat.formatHHMM(end)}`;
    const hours = DateFormat.formatDuration(sumWorkDurations);
    const breakTime = DateFormat.formatDuration(sumBreakDurations);

    if (withDetails) {
        writeRowBold({ pdf, pdfState }, date, time, hours, breakTime);
        writeLine({ pdf, pdfState });

        if (leadingBreakDuration) {
            writeBreak({ pdf, pdfState }, leadingBreakDuration);
        }
        for (let i = 0; i < workDay.workEntries.length - 1; i++) {
            writeEntry({ pdf, pdfState }, workDay.workEntries[i]);
            writeBreak({ pdf, pdfState }, breakDurations[i]);
        };
        writeEntry({ pdf, pdfState }, workDay.workEntries[workDay.workEntries.length - 1]);
        if (trailingBreakDuration) {
            writeBreak({ pdf, pdfState }, trailingBreakDuration);
        }

        pdfState.newLine();
    } else {
        writeRow({ pdf, pdfState }, date, time, hours, breakTime);
    };
};

const writeEntry = ({ pdf, pdfState }, entry) => {
    const start = new Date(entry.start);
    const end = new Date(entry.end);
    const diff = TimeCalculator.computeTimeDifference(start, end);

    const date = '';
    const time = `${DateFormat.formatHHMM(start)} - ${DateFormat.formatHHMM(end)}`;
    const hours = DateFormat.formatDuration(diff);
    const breakTime = '';

    writeRow({ pdf, pdfState }, date, time, hours, breakTime);
};

const writeColumnHeader = ({ pdf, pdfState }) => {
    writeRowBold({ pdf, pdfState }, 'Tag', 'Zeiten', 'Arbeit', 'Pause');
};

const writeBreak = ({ pdf, pdfState }, breakTime) => {
    let bT = DateFormat.formatDuration(breakTime);

    writeRow({ pdf, pdfState }, '', '', '', bT);
};

const writeRowBold = ({ pdf, pdfState }, date, time, hours, breakTime) => {
    pdfState.setFontBold();

    writeRow({ pdf, pdfState }, date, time, hours, breakTime);

    pdfState.setFontNormal();
}

const writeRow = ({ pdf, pdfState }, date, time, hours, breakTime) => {
    writeDateColumn({ pdf, pdfState }, date);
    writeTimeColumn({ pdf, pdfState }, time);
    writeHoursColumn({ pdf, pdfState }, hours);
    writeBreakColumn({ pdf, pdfState }, breakTime);

    pdfState.newLine();
};

const writeDateColumn = ({ pdf, pdfState }, text) => {
    let page = pdf.getPage(pdf.getPages().length - 1);
    let opts = pdfState.getOptionsDateColumn();

    write(page, opts, text);
};

const writeTimeColumn = ({ pdf, pdfState }, text) => {
    let page = pdf.getPage(pdf.getPages().length - 1);
    let opts = pdfState.getOptionsTimeColumn();

    write(page, opts, text);
};

const writeHoursColumn = ({ pdf, pdfState }, text) => {
    let page = pdf.getPage(pdf.getPages().length - 1);
    let opts = pdfState.getOptionsHoursColumn();

    write(page, opts, text);
};

const writeBreakColumn = ({ pdf, pdfState }, text) => {
    let page = pdf.getPage(pdf.getPages().length - 1);
    let opts = pdfState.getOptionsBreakColumn();

    write(page, opts, text);
};

const write = (page, options, text) => {
    page.drawText(text, options);
};

const writeLine = ({ pdf, pdfState }) => {
    pdfState.currentLine -= 0.7;

    let page = pdf.getPage(pdf.getPages().length - 1);
    let opts = pdfState.getOptions();

    page.drawLine({
        start: { x: opts.x, y: opts.y },
        end: { x: (opts.x + pdfState.columnWidth * 4), y: opts.y },
        thickness: 1,
        color: opts.color,
        opacity: 1,
    });

    pdfState.currentLine += 0.8;
}

export default generatePDFfromRecords
